import { useState } from 'react';
import { X, Loader2, Plug, CheckCircle, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { productsService } from '../../services/products.service';
import { usersService } from '../../services/users.service';
import {
  useCreateIntegration,
  useUpdateIntegration,
  useTestConnection,
} from '../../hooks/useIntegrations';
import { IntegrationConfigFields } from './IntegrationConfigFields';

interface IntegrationFormProps {
  integration?: any;
  onClose: () => void;
}

const INTEGRATION_TYPES = [
  { value: 'DOCUGEN', label: 'DocuGen' },
  { value: 'WISEJUS', label: 'WiseJus' },
];

export function IntegrationForm({ integration, onClose }: IntegrationFormProps) {
  const isEditing = !!integration;

  const [name, setName] = useState(integration?.name || '');
  const [type, setType] = useState(integration?.type || 'DOCUGEN');
  const [config, setConfig] = useState<Record<string, any>>(
    integration ? { ...integration.config, apiKey: '' } : { baseUrl: '', apiKey: '' }
  );
  const [productId, setProductId] = useState<number | ''>(integration?.productId || '');
  const [defaultAssignedUserId, setDefaultAssignedUserId] = useState<number | ''>(
    integration?.defaultAssignedUserId || ''
  );
  const [defaultOriginId, setDefaultOriginId] = useState<number | '' | null>(
    integration?.defaultOriginId || ''
  );
  const [active, setActive] = useState(integration?.active ?? false);
  const [syncIntervalMinutes, setSyncIntervalMinutes] = useState(
    integration?.syncIntervalMinutes ?? 60
  );
  const [freeDaysThreshold, setFreeDaysThreshold] = useState(integration?.freeDaysThreshold ?? 8);

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsService.list(),
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.list(),
  });

  const { data: origins } = useQuery({
    queryKey: ['product', productId, 'origins'],
    queryFn: () => productsService.getOrigins(Number(productId)),
    enabled: !!productId,
  });

  const createMutation = useCreateIntegration();
  const updateMutation = useUpdateIntegration();
  const testMutation = useTestConnection();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const data = {
      name,
      type,
      config,
      productId: Number(productId),
      defaultAssignedUserId: Number(defaultAssignedUserId),
      defaultOriginId: defaultOriginId ? Number(defaultOriginId) : null,
      active,
      syncIntervalMinutes,
      freeDaysThreshold,
    };

    if (isEditing) {
      updateMutation.mutate(
        { id: integration.id, data },
        {
          onSuccess: () => {
            toast.success('Integração atualizada');
            onClose();
          },
          onError: (err: any) => toast.error(err.response?.data?.error || 'Erro ao atualizar'),
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          toast.success('Integração criada');
          onClose();
        },
        onError: (err: any) => toast.error(err.response?.data?.error || 'Erro ao criar'),
      });
    }
  }

  function handleTestConnection() {
    const testConfig = { ...config };
    if (isEditing && !testConfig.apiKey) {
      toast.error('Informe a API Key para testar a conexão');
      return;
    }

    testMutation.mutate(
      { type, config: testConfig },
      {
        onSuccess: () => toast.success('Conexão estabelecida com sucesso!'),
        onError: (err: any) =>
          toast.error(err.response?.data?.error || 'Falha na conexão — verifique as credenciais'),
      }
    );
  }

  const productUsers = users?.users
    ? users.users.filter(
        (u: any) => u.active && u.products?.some((p: any) => p.productId === Number(productId))
      )
    : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-secondary-200">
          <div className="flex items-center gap-2">
            <Plug className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-secondary-900">
              {isEditing ? 'Editar Integração' : 'Nova Integração'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary-100">
            <X className="w-5 h-5 text-secondary-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">Nome</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="DocuGen Produção"
              className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">Tipo</label>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setConfig({ baseUrl: '', apiKey: '' });
              }}
              disabled={isEditing}
              className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-secondary-50"
            >
              {INTEGRATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t border-secondary-200 pt-4">
            <IntegrationConfigFields
              type={type}
              config={config}
              onChange={setConfig}
              isEditing={isEditing}
            />
          </div>

          <div className="border-t border-secondary-200 pt-4 space-y-3">
            <h4 className="text-sm font-medium text-secondary-700">Destino no CRM</h4>

            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">Produto</label>
              <select
                value={productId}
                onChange={(e) => {
                  setProductId(Number(e.target.value));
                  setDefaultAssignedUserId('');
                  setDefaultOriginId('');
                }}
                required
                className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Selecione...</option>
                {(products?.data || products || []).map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">
                Vendedor Padrão
              </label>
              <select
                value={defaultAssignedUserId}
                onChange={(e) => setDefaultAssignedUserId(Number(e.target.value))}
                required
                className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Selecione...</option>
                {productUsers.map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">
                Origem do Lead
              </label>
              <select
                value={defaultOriginId ?? ''}
                onChange={(e) => setDefaultOriginId(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Nenhuma</option>
                {(origins?.data || origins || []).map((o: any) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-secondary-200 pt-4 space-y-3">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-secondary-600">Ativo</label>
              <button
                type="button"
                onClick={() => setActive(!active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${active ? 'bg-primary-600' : 'bg-secondary-300'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">
                Intervalo de Sync (minutos)
              </label>
              <input
                type="number"
                min={1}
                max={1440}
                value={syncIntervalMinutes}
                onChange={(e) => setSyncIntervalMinutes(Number(e.target.value))}
                className="w-32 rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">
                Alerta de cliente free (dias)
              </label>
              <p className="text-xs text-secondary-400 mb-1">
                Sinalizar leads free criados há mais de X dias
              </p>
              <input
                type="number"
                min={1}
                max={365}
                value={freeDaysThreshold}
                onChange={(e) => setFreeDaysThreshold(Number(e.target.value))}
                className="w-32 rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="border-t border-secondary-200 pt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
            >
              {testMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : testMutation.isSuccess ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : testMutation.isError ? (
                <XCircle className="w-4 h-4 text-red-600" />
              ) : (
                <Plug className="w-4 h-4" />
              )}
              Testar Conexão
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-secondary-600 bg-secondary-100 rounded-lg hover:bg-secondary-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
